import React, { useCallback, useMemo, useState } from "react";
import { GridApi } from "ag-grid-community";
import { Card, Text, Grid, Flex, Button, toast } from "@jsq/ds/core";
import { ComplianceObjectType } from "~/graphql/supergraphEnums";
import {
  useGetDocumentExtractedDataQuery,
  useGetProfileInfoQuery,
  useUpdateIndividualProfileMutation,
  useUpdateInstitutionProfileMutation,
} from "~/graphql/supergraphHooks";
import { defaultColDef } from "~/pages/Compliance/CaseDetailsDrawer/PaymentInstructions/BeneficiarySummary/gridConfig";
import { getProfileName } from "~/pages/Compliance/CaseDetailsDrawer/Profiles/utils";
import { useCurrentCaseContext } from "~/pages/Compliance/context/CurrentCaseContext";
import { findUsStateName } from "~/pages/Compliance/documents/CaseCreationPreview/stateMatcher";
import { createColDefs, ConflictRowData, gridOptions } from "./gridConfig";
import { compareValues } from "./profileDataComparison";
import { ProfileConflictResolverBaseContainer } from "./styles_vanilla.css";
import {
  buildIndividualProfileInput,
  buildInstitutionProfileInput,
  getSelectedValuesFromGrid,
} from "./utils";

type ProfileConflictResolverProps = {
  profileId: number;
  documentId: number;
  onClose: () => void;
};

export const ProfileConflictResolver: React.FC<ProfileConflictResolverProps> = ({
  profileId,
  documentId,
  onClose,
}) => {
  const caseContext = useCurrentCaseContext();
  const caseId = caseContext.caseId;
  const [gridApi, setGridApi] = useState<GridApi<ConflictRowData> | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const profileDetails = useGetProfileInfoQuery({
    variables: {
      id: profileId,
    },
  });

  const documentData = useGetDocumentExtractedDataQuery({
    variables: {
      docId: documentId,
    },
  });

  const [updateIndividualProfile, { error: updateIndividualError }] =
    useUpdateIndividualProfileMutation({
      onError: (error) => toast.error(`Failed to update profile: ${error.message}`),
    });

  const [updateInstitutionProfile, { error: updateInstitutionError }] =
    useUpdateInstitutionProfileMutation({
      onError: (error) => toast.error(`Failed to update profile: ${error.message}`),
    });

  const isIndividual =
    profileDetails.data?.complianceProfile?.complianceObjectType ===
    ComplianceObjectType.Individual;

  const profile = profileDetails.data?.complianceProfile;
  const profileName = profile
    ? getProfileName({
        id: profile.id,
        complianceObjectType: profile.complianceObjectType,
        firstName: profile.firstName,
        middleName: profile.middleName,
        lastName: profile.lastName,
        legalName: profile.legalName,
      })
    : "--";

  const documentName = documentData?.data?.document?.name || "--";

  const primaryAddress = profileDetails.data?.complianceProfile?.addresses?.find(
    (addr) => addr.isPrimaryDomicile,
  );

  const profileData = useMemo(() => {
    return {
      dateOfBirth: isIndividual ? profileDetails.data?.complianceProfile?.dateOfBirth : null,
      dateOfOrg: isIndividual
        ? null
        : profileDetails.data?.complianceProfile?.dateOfOrganization,
      streetAddress: primaryAddress
        ? [primaryAddress.street, primaryAddress.street2, primaryAddress.street3]
            .filter(Boolean)
            .join(", ")
        : null,
      city: primaryAddress?.city,
      state: primaryAddress?.state?.name || primaryAddress?.province,
      zipCode: primaryAddress?.postalCode,
    };
  }, [profileDetails.data, isIndividual, primaryAddress]);

  const extractedData = useMemo(() => {
    return documentData?.data?.document?.agentExtractedData;
  }, [documentData]);

  const getExtractedValue = useCallback(
    (key: string): string => {
      const value = extractedData?.[key];
      return typeof value === "string" ? value : "--";
    },
    [extractedData],
  );

  const extractedStreetAddress = useMemo((): string => {
    const street = extractedData?.street;
    const street2 = extractedData?.street2;
    const street3 = extractedData?.street3;
    const parts = [street, street2, street3].filter((part) => typeof part === "string" && part);
    return parts.length > 0 ? parts.join(", ") : "--";
  }, [extractedData]);

  const toComparable = (displayValue: string): string =>
    displayValue === "--" ? "" : displayValue;

  const rowData: ConflictRowData[] = useMemo(() => {
    const allRows: ConflictRowData[] = [
      ...(isIndividual
        ? [
            {
              fieldName: "Date of birth",
              currentValue: profileData.dateOfBirth || "--",
              extractedValue: getExtractedValue("date_of_birth"),
              selectedSource: "extracted" as const,
            },
          ]
        : [
            {
              fieldName: "Date of organization",
              currentValue: profileData.dateOfOrg || "--",
              extractedValue: getExtractedValue("date_of_organization"),
              selectedSource: "extracted" as const,
            },
          ]),
      {
        fieldName: "Street address",
        currentValue: profileData.streetAddress || "--",
        extractedValue: extractedStreetAddress,
        selectedSource: "extracted" as const,
      },
      {
        fieldName: "City",
        currentValue: profileData.city || "--",
        extractedValue: getExtractedValue("city"),
        selectedSource: "extracted" as const,
      },
      {
        fieldName: "State",
        currentValue: profileData.state || "--",
        extractedValue:
          findUsStateName(getExtractedValue("state")) || getExtractedValue("state"),
        selectedSource: "extracted" as const,
      },
      {
        fieldName: "Zip code",
        currentValue: profileData.zipCode || "--",
        extractedValue: getExtractedValue("postal_code"),
        selectedSource: "extracted" as const,
      },
    ];

    return allRows.filter((row) =>
      compareValues(toComparable(row.currentValue), toComparable(row.extractedValue)),
    );
  }, [isIndividual, profileData, getExtractedValue, extractedStreetAddress]);

  const handleApplySelection = async () => {
    if (!gridApi) {
      toast.error("Grid not initialized");
      return;
    }

    if (!profile) {
      toast.error("Profile data not loaded");
      return;
    }

    setIsSubmitting(true);

    try {
      const selectedValues = getSelectedValuesFromGrid(gridApi);

      if (isIndividual) {
        const input = buildIndividualProfileInput(selectedValues, profile, caseId);

        await updateIndividualProfile({
          variables: {
            data: input,
            profileId: profileId,
            resolveConflictsOnDocId: documentId,
          },
          refetchQueries: ["GetComplianceCase"],
        });
      } else {
        const input = buildInstitutionProfileInput(selectedValues, profile, caseId);

        await updateInstitutionProfile({
          variables: {
            data: input,
            profileId: profileId,
            resolveConflictsOnDocId: documentId,
          },
          refetchQueries: ["GetComplianceCase"],
        });
      }

      if (!updateInstitutionError && !updateIndividualError)
        toast.success(`${profileName}'s profile has been successfully updated`);
      onClose();
    } catch (_error) {
      toast.error("An error occurred while updating the profile");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGridReady = (event: { api: GridApi<ConflictRowData> }) => {
    setGridApi(event.api);
  };

  return (
    <Flex flexDirection="column" gap={24}>
      <Card className={ProfileConflictResolverBaseContainer}>
        <Text variant="h3">Resolve profile data conflicts</Text>
        <Text variant="regular-md">
          Review the conflicts found between the current profile and extracted document data.
          Select the data version you’d like to apply.
        </Text>
        <Grid
          columnDefs={createColDefs({ profileName, documentName })}
          rowData={rowData}
          defaultColDef={defaultColDef}
          gridOptions={gridOptions}
          onGridReady={handleGridReady}
        />
      </Card>
      <Flex flexDirection="row-reverse" gap={10}>
        <Button onClick={handleApplySelection} disabled={isSubmitting} isLoading={isSubmitting}>
          Apply selection to profile
        </Button>
        <Button onClick={onClose} variant="neutral" disabled={isSubmitting}>
          Cancel
        </Button>
      </Flex>
    </Flex>
  );
};
